import { Trash } from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { MultiValue } from 'react-select';
import { useRouting } from '../../../hooks/route';
import {
    deleteFlow,
    getFlowLabels,
    getFlowsPaged,
    getLatest,
    setLatest,
} from '../../../sources/flow';
import translations from '../../../translations';
import type { OrderDirection } from '../../../types/api';
import type { FlowResponseAPI } from '../../../types/flow';
import { isTooltipRequired } from '../../../utils/display';

import { stringReplace } from '../../../utils/string';
import ButtonPrimary from '../../buttons/ButtonPrimary';
import SearchInput from '../../generic/SearchInput';
import Sortable from '../../generic/Sortable';
import Table, { type TableColumnList } from '../../generic/Table';
import FooterButtons from '../../generic/modal/FooterButtons';
import GenericModal from '../../generic/modal/GenericModal';
import ComponentWithTooltip from '../../generic/tooltip/ComponentWithTooltip';
import InlineInput from '../../inputs/InlineInput';
import Loader from '../../loader/Loader';
import FlowLabels from '../FlowLabels';
import { SecurityIcon } from '../SecurityIcon';
import { isPublic } from '../../../utils/flow';
import FlowUsers from './FlowUsers';
import GettingStarted from './GettingStarted';
import NewFlowModal from './NewFlowModal';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';
import { useUser } from '../../UserProvider';
import ExportFlow from '../export/ExportFlow';
import ButtonDefault from '../../buttons/ButtonDefault';
import { ExIcon } from '@boomi/exosphere';
import ModalBody from '../../generic/modal/ModalBody';
import ModalHeader from '../../generic/modal/ModalHeader';
import ImportFlowModal from '../import/ImportFlowModal';
import FlowFiltersPopup from './FlowFiltersPopup';

interface Props {
    isEditorActive: boolean;
}

const FlowList = ({ isEditorActive }: Props) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [allFlowLabels, setAllFlowLabels] = useState<string[]>([]);
    const [filterLabels, setFilterLabels] = useState<string[]>([]);
    const [filterIsPublic, setFilterIsPublic] = useState<boolean | null>(null);
    const [filteredFlows, setFilteredFlows] = useState<FlowResponseAPI[]>([]);
    const [sortProperty, setSortProperty] = useState('dateModified');
    const [sortDirection, setSortDirection] = useState<OrderDirection>('DESC');

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showNewFlowModal, setShowNewFlowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFlow, setSelectedFlow] = useState<FlowResponseAPI | null>(null);

    const [showFlowExportModal, setShowFlowExportModal] = useState(false);
    const [showFlowImportModal, setShowFlowImportModal] = useState(false);

    const [descriptionEditorOpen, setDescriptionEditorOpen] = useState<string | null>(null);

    const user = useUser();
    const { openFlow } = useRouting();
    const dispatchNotification = useNotificationDispatch();

    const flowListRef = useRef(null);

    const [pagination, setPagination] = useState({
        page: 1,
        total: 0,
        pageSize: 20,
    });

    /** Used to make sure we only show the getting started page AFTER loading flows */
    const [hasFetchedFlowList, setHasFetchedFlowList] = useState(false);

    const showGettingStarted =
        hasFetchedFlowList &&
        !isLoading &&
        searchQuery === '' &&
        filterLabels.length === 0 &&
        filterIsPublic === null &&
        pagination.total === 0;

    const fetchFlowLabels = useCallback(async () => {
        try {
            const response = await getFlowLabels();
            setAllFlowLabels(response);
        } catch (_error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error: 'Unable to load flow labels',
                },
            });
        }
    }, [dispatchNotification]);

    const fetchFlows = useCallback(async () => {
        setIsLoading(true);

        try {
            const filter = {
                search: searchQuery,
                page: pagination.page,
                orderBy: sortProperty,
                orderDirection: sortDirection,
                isPublic: filterIsPublic ?? undefined,
            };

            const response = await getFlowsPaged(filter, filterLabels);

            setFilteredFlows(response.items);
            setPagination(response._meta);
        } catch (_error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error: 'Unable to load flows',
                },
            });
        } finally {
            setIsLoading(false);
            setHasFetchedFlowList(true);
        }
    }, [
        pagination.page,
        dispatchNotification,
        searchQuery,
        filterLabels,
        filterIsPublic,
        sortDirection,
        sortProperty,
    ]);

    useEffect(() => {
        if (!isEditorActive) {
            return;
        }

        fetchFlowLabels();
    }, [fetchFlowLabels, isEditorActive]);

    useEffect(() => {
        if (!isEditorActive) {
            return;
        }

        fetchFlows();
    }, [fetchFlows, isEditorActive]);

    const onClickNewFlow = () => {
        setShowNewFlowModal(true);
    };

    const onFlowCreated = (flowId: string) => {
        setShowNewFlowModal(false);
        openFlow(flowId);
    };

    const onClickExportFlow = () => {
        setShowFlowExportModal(true);
    };

    const onClickImportFlow = () => {
        setShowFlowImportModal(true);
    };

    const onClickEditFlow = (flow: FlowResponseAPI) => {
        openFlow(flow.id.id);
    };

    const onClickDeleteFlow = (flow: FlowResponseAPI) => {
        setShowDeleteModal(true);
        setSelectedFlow(flow);
    };

    const deleteFlowConfirmation = async () => {
        if (!selectedFlow?.id.id) {
            return;
        }

        await deleteFlow(selectedFlow.id.id);

        setShowDeleteModal(false);
        fetchFlows();
        fetchFlowLabels();
    };

    const updateFlowLabels = async (labels: string[], flow: FlowResponseAPI) => {
        const flowData = await getLatest(flow.id.id);
        const editedFlow = {
            ...flowData,
            tags: labels,
        };

        await setLatest(editedFlow);

        fetchFlows();
        fetchFlowLabels();
    };

    const handleFilterLabelsChange = (options: MultiValue<{ value: string; label: string }>) => {
        if (options) {
            setFilterLabels(options.map((o) => o.label));
        } else {
            setFilterLabels([]);
        }
    };

    const updateFlowDescription = async (flowId: string, value: string) => {
        const flowData = await getLatest(flowId);
        const editedFlow = {
            ...flowData,
            developerSummary: value,
        };

        await setLatest(editedFlow);

        fetchFlows();
    };

    const controlsBar = (
        <div className="flow-controls-bar">
            <div className="controls-left">
                <SearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder={translations.HOME_flow_search_placeholder}
                />
                <FlowFiltersPopup
                    allFlowLabels={allFlowLabels}
                    filterLabels={filterLabels}
                    filterIsPublic={filterIsPublic}
                    onFilterLabelsChange={handleFilterLabelsChange}
                    onFilterIsPublicChange={setFilterIsPublic}
                />
            </div>
            <div className="controls-right flex">
                <ButtonPrimary onClick={onClickNewFlow} title={translations.HOME_new_flow}>
                    <ExIcon icon="Add new" label={translations.HOME_new_flow} />
                    <span className="button-text">{translations.HOME_new_flow}</span>
                </ButtonPrimary>
                <ButtonDefault onClick={onClickExportFlow} title={translations.HOME_export_flow}>
                    <ExIcon icon="Download" label={translations.HOME_export_flow} />
                    <span className="button-text">{translations.HOME_export_flow}</span>
                </ButtonDefault>
                <ButtonDefault onClick={onClickImportFlow} title={translations.HOME_import_flow}>
                    <ExIcon icon="Upload" label={translations.HOME_import_flow} />
                    <span className="button-text">{translations.HOME_import_flow}</span>
                </ButtonDefault>
            </div>
        </div>
    );

    const columns: TableColumnList<FlowResponseAPI> = [
        {
            renderHeader: () => (
                <Sortable
                    onSort={(direction) => {
                        setSortProperty('developerName');
                        setSortDirection(direction);
                    }}
                    direction={sortProperty === 'developerName' ? sortDirection : null}
                >
                    {translations.COMMON_TABLE_name}
                </Sortable>
            ),
            renderCell: ({ item }) => {
                return (
                    <div className="flow-name-with-auth">
                        <SecurityIcon
                            isPublic={isPublic(item)}
                            testId={`auth-${item.id.id || item.id.versionId}`}
                            label={
                                isPublic(item)
                                    ? translations.FLOW_auth_public_label
                                    : translations.FLOW_auth_secured_label
                            }
                        />
                        {isTooltipRequired(item.developerName, 25) ? (
                            <ComponentWithTooltip
                                trigger={['hover', 'focus']}
                                showDelay={500}
                                fadeTime={0.2}
                                tooltipPlacement="top"
                                tooltipContent={item.developerName}
                                wrapperClass="width-expand-to-full"
                            >
                                <button
                                    className="link-emulate overflow-ellipsis"
                                    onClick={() => onClickEditFlow(item)}
                                    type="button"
                                >
                                    {item.developerName}
                                </button>
                            </ComponentWithTooltip>
                        ) : (
                            <button
                                className="link-emulate overflow-ellipsis"
                                onClick={() => onClickEditFlow(item)}
                                type="button"
                            >
                                {item.developerName}
                            </button>
                        )}
                    </div>
                );
            },
        },
        {
            renderHeader: () => (
                <Sortable
                    defaultDirection={'ASC'}
                    onSort={(direction) => {
                        setSortProperty('developerSummary');
                        setSortDirection(direction);
                    }}
                    direction={sortProperty === 'developerSummary' ? sortDirection : null}
                >
                    {translations.COMMON_TABLE_description}
                </Sortable>
            ),
            renderCell: ({ item }) => (
                <InlineInput
                    defaultValue={item.developerSummary ?? ''}
                    saveInputValue={(value) => updateFlowDescription(item.id.id, value)}
                    editButtonTitle={`Edit ${item.developerName} description`}
                    confirmButtonTitle={`Confirm ${item.developerName} description`}
                    cancelButtonTitle={`Cancel ${item.developerName} description`}
                    isOpen={descriptionEditorOpen === item.id.id}
                    setIsOpen={(isOpen) => {
                        if (isOpen) {
                            setDescriptionEditorOpen(item.id.id);
                        } else {
                            setDescriptionEditorOpen(null);
                        }
                    }}
                />
            ),
            cellClassName: 'generic-cell-simple',
        },
        {
            renderHeader: () => (
                <Sortable
                    onSort={(direction) => {
                        setSortProperty('dateModified');
                        setSortDirection(direction);
                    }}
                    direction={sortProperty === 'dateModified' ? sortDirection : null}
                    defaultDirection={'ASC'}
                >
                    {translations.COMMON_TABLE_last_modified}
                </Sortable>
            ),
            renderCell: ({ item }) =>
                new Date(item.dateModified).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                }),
            size: '11rem',
        },
        {
            renderHeader: () => translations.COMMON_TABLE_users,
            renderCell: ({ item }) => <FlowUsers flowId={item.id.id} />,
            size: '12rem',
        },
        {
            renderHeader: () => translations.COMMON_TABLE_labels,
            renderCell: ({ item }) => (
                <FlowLabels
                    currentLabels={item.tags}
                    allFlowLabels={allFlowLabels}
                    saveFlowLabels={(labels: string[]) => updateFlowLabels(labels, item)}
                />
            ),
        },
        {
            renderHeader: () => translations.COMMON_TABLE_actions,
            renderCell: ({ item }) => (
                <div className="action-btn-wrapper">
                    <button
                        title={`${translations.COMMON_delete} ${item.developerName}`}
                        className="table-icon table-icon-delete"
                        onClick={() => onClickDeleteFlow(item)}
                        aria-label={`${translations.COMMON_delete} ${item.developerName}`}
                        type="button"
                    >
                        <Trash />
                    </button>
                </div>
            ),
            size: '5rem',
        },
    ];

    const onConfirmDeleteCancel = () => {
        setSelectedFlow(null);
        setShowDeleteModal(false);
    };

    const renderBody = () => {
        const confirmMessage = stringReplace(
            translations.HOME_delete_confirm,
            selectedFlow?.developerName ?? '',
        );

        return (
            <div className="container-fluid">
                <div className="message-line">{confirmMessage}</div>
                <div className="message-line">{translations.COMMON_cannot_be_undone}</div>
            </div>
        );
    };

    const renderFooter = () => (
        <FooterButtons
            confirm={deleteFlowConfirmation}
            confirmButtonText={translations.COMMON_delete}
            confirmButtonClasses="btn-danger"
            cancel={onConfirmDeleteCancel}
            cancelButtonText={translations.COMMON_cancel}
        />
    );

    const renderMainContent = () => {
        if (showGettingStarted) {
            return (
                <GettingStarted
                    onNewFlowClick={onClickNewFlow}
                    onImportFlowClick={onClickImportFlow}
                />
            );
        }
        if (isLoading) {
            return (
                <>
                    {controlsBar}
                    <Loader message={translations.HOME_loading_flows} />
                </>
            );
        }
        return (
            <>
                {controlsBar}
                <Table
                    items={filteredFlows}
                    columns={columns}
                    pagination={{
                        ...pagination,
                        changePage: (page) =>
                            setPagination((pagination) => ({ ...pagination, page })),
                    }}
                    isLoading={isLoading}
                />
            </>
        );
    };

    return (
        <div ref={flowListRef}>
            {showDeleteModal ? (
                <GenericModal
                    className="confirm-modal"
                    onHide={onConfirmDeleteCancel}
                    renderHeader={() => (
                        <h4 className="modal-title">
                            {translations.FLOW_delete_confirmation_title}
                        </h4>
                    )}
                    renderBody={renderBody}
                    renderFooter={renderFooter}
                    container={flowListRef.current}
                />
            ) : null}
            {showFlowExportModal && (
                <GenericModal
                    className="export-flow-modal modal-dialog-small"
                    onHide={() => setShowFlowExportModal(false)}
                    container={flowListRef.current}
                >
                    <ModalHeader onHide={() => setShowFlowExportModal(false)}>
                        <h4 className="modal-title">
                            {translations.IMPORT_EXPORT_export_flow_section_heading}
                        </h4>
                    </ModalHeader>
                    <ModalBody>
                        <ExportFlow />
                    </ModalBody>
                </GenericModal>
            )}
            <ImportFlowModal
                show={showFlowImportModal && flowListRef.current !== null}
                onHide={() => setShowFlowImportModal(false)}
                modalContainer={flowListRef.current}
                onImportSuccess={() => {
                    fetchFlows();
                    fetchFlowLabels();
                }}
            />
            <NewFlowModal
                show={showNewFlowModal}
                onCancel={() => setShowNewFlowModal(false)}
                onError={(error) => {
                    dispatchNotification({
                        type: 'notifyError',
                        payload: {
                            error,
                        },
                    });
                }}
                onFlowCreated={onFlowCreated}
                ref={flowListRef.current}
            />
            <h1>
                {translations.HOME_welcome_back}, {user?.firstName}
            </h1>
            <h4>{translations.HOME_heading}</h4>
            {renderMainContent()}
        </div>
    );
};

export default FlowList;
